import { useEffect, useRef } from 'react';
import { AppService } from '../../services';
import { getDataFromUrl } from '../../utils';
import { baseLayerChangeEvent, getMapBaseLayer } from './base-layer';
import { useLayers } from './layers';
export const facilitiesSourceIds = ['NewFacilities', 'NewFacilitiesSimplified'];

// The base styles carry their own copy of the facilities, renamed so that it never collides with
// the overlay styles, some of which reuse the `NewFacilities` name for other tiles (IDFM, v3).
export function toEmbeddedFacilitiesSourceId(sourceId, kind) {
  return `embedded_${kind}_${sourceId}`;
}
export const embeddedFacilitiesLayerIdSuffix = '_embedded';

/** Set on each embedded facilities layer, to the kind of base style (`base`, `dark`) it comes from. */
export const embeddedFacilitiesMetadataKey = 'geovelo:embedded-facilities';
// The embedded facilities are the ones this overlay draws: the other overlays (IDFM, v3) show
// something else, and must still be drawn on top of the map.
const embeddableOverlayStyleDir = 'facilities_v2-vector';
function getEmbeddedFacilitiesKind(baseLayer) {
  switch (baseLayer) {
    case 'geovelo':
    case 'aerial':
    case 'ign':
      return 'base';
    case 'dark':
      return 'dark';
    default:
      return null;
  }
}
export function getEmbeddedFacilitiesLayerKind(layer) {
  const metadata = layer.metadata;
  return metadata?.[embeddedFacilitiesMetadataKey] || null;
}
export function useFacilities(map) {
  const initialized = useRef(false);
  const facilityVectorLayers = useRef(null);
  const layerOpacities = useRef({});
  const {
    getLayerOpacities
  } = useLayers(map);
  const enabledRef = useRef(false);
  const embeddableRef = useRef(false);
  useEffect(() => {
    if (!map) return;
    const handleBaseLayerChange = () => {
      if (initialized.current) toggle(enabledRef.current);
    };
    map.on(baseLayerChangeEvent, handleBaseLayerChange);
    return () => {
      map.off(baseLayerChangeEvent, handleBaseLayerChange);
    };
  }, [map]);
  async function init(enabled, useIDFMStyle = false) {
    const {
      tileServer: {
        url: tileServerUrl,
        styleDirs: {
          facilities: styleDir,
          facilitiesIDFM
        }
      }
    } = AppService.environment;
    embeddableRef.current = !useIDFMStyle && styleDir === embeddableOverlayStyleDir;
    const {
      sources,
      layers
    } = await getDataFromUrl(`${tileServerUrl}/styles/${useIDFMStyle ? facilitiesIDFM : styleDir}/style.json`);
    Object.keys(sources).map(key => {
      if (!map?.getSource(key)) {
        map?.addSource(key, sources[key]);
      }
    });
    facilityVectorLayers.current = layers;
    layerOpacities.current = {
      ...getLayerOpacities(facilityVectorLayers.current, 'NewFacilities'),
      ...getLayerOpacities(facilityVectorLayers.current, 'NewFacilitiesSimplified')
    };
    toggle(enabled);
    initialized.current = true;
  }
  function toggleEmbedded(kind, enabled) {
    if (!map) return false;
    let found = false;
    map.getStyle()?.layers?.forEach(layer => {
      const layerKind = getEmbeddedFacilitiesLayerKind(layer);
      if (!layerKind) return;
      if (layerKind === kind) found = true;
      map.setLayoutProperty(layer.id, 'visibility', enabled && layerKind === kind ? 'visible' : 'none');
    });
    return found;
  }
  function toggle(enabled) {
    if (!map) return;
    const style = map.getStyle();
    if (!style) return;
    enabledRef.current = enabled;

    // Falls back to the overlay when the current base style carries no facilities (toner, or a
    // tileserver still serving styles without them).
    const kind = embeddableRef.current ? getEmbeddedFacilitiesKind(getMapBaseLayer(map)) : null;
    const embedded = toggleEmbedded(kind, enabled);
    const overlayEnabled = enabled && !embedded;
    if (overlayEnabled) {
      facilityVectorLayers.current?.forEach(layer => {
        if (!map.getLayer(layer.id)) map.addLayer(layer, 'facilities');
      });
    }
    style.layers?.forEach(layer => {
      if ('source' in layer && (layer.source === 'NewFacilities' || layer.source === 'NewFacilitiesSimplified')) {
        const layerOpacity = layerOpacities.current[layer.id];
        if (layer.type === 'fill') {
          map.setPaintProperty(layer.id, 'fill-opacity', overlayEnabled ? layerOpacity !== undefined ? layerOpacity : 1 : 0);
        } else if (layer.type === 'line') {
          map.setPaintProperty(layer.id, 'line-opacity', overlayEnabled ? layerOpacity !== undefined ? layerOpacity : 1 : 0);
        } else {
          map.setLayoutProperty(layer.id, 'visibility', overlayEnabled ? 'visible' : 'none');
        }
      }
    });
  }
  function destroy() {
    enabledRef.current = false;
    embeddableRef.current = false;
    if (map) {
      toggleEmbedded(null, false);
      const style = map.getStyle();
      if (!style) {
        initialized.current = false;
        facilityVectorLayers.current = null;
        layerOpacities.current = {};
        return;
      }
      const sourceIds = new Set();
      facilityVectorLayers.current?.forEach(layer => {
        if ('source' in layer) sourceIds.add(layer.source);
      });
      style.layers?.filter(layer => 'source' in layer && (layer.source === 'NewFacilities' || layer.source === 'NewFacilitiesSimplified') || facilityVectorLayers.current?.some(({
        id
      }) => id === layer.id)).slice().reverse().forEach(layer => {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
        if ('source' in layer) sourceIds.add(layer.source);
      });
      [...sourceIds].forEach(sourceId => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
    }
    initialized.current = false;
    facilityVectorLayers.current = null;
    layerOpacities.current = {};
  }
  return {
    initialized: initialized.current || false,
    init,
    toggle,
    destroy
  };
}